import dotenv from 'dotenv'
import isPlainObject from 'lodash.isplainobject'

if (process.env.NODE_ENV !== 'production') {
  dotenv.config()
}

// values each env are whitelisted to ignore
const whitelist: Record<string, string[]> = {
  NSWFA: [
    'BEEKEEPER_FORM_ID',
    'BEEKEEPER_RECEIPT_LINK',
    'ACTIVITY_REPORT_FORM_ID',
    'BEEKEEPER_REGISTRATION_FORM_ID',
  ],
  DPI: [
    'FOOD_LICENCE_APPLICATION_FORM_ID',
    'AI_FOOD_LICENCE_APPLICATION_FORM_ID',
    'FOOD_NOTIFICATION_FORM_ID',
    'VEHICLE_VESSEL_VARIATION_FORM_ID',
    'COMPLAINTS_FORM_ID',
    'RESOURCE_ORDER_FORM_ID',
    'ACTIVITY_REPORT_FORM_ID',
    'VEHICLE_VESSEL_VARIATION_FORM_ID',
    'LGA_RESOURCE_ORDER_FORM_ID',
  ],
  FRP: [
    'FOOD_LICENCE_APPLICATION_FORM_ID',
    'AI_FOOD_LICENCE_APPLICATION_FORM_ID',
    'FOOD_NOTIFICATION_FORM_ID',
    'VEHICLE_VESSEL_VARIATION_FORM_ID',
    'COMPLAINTS_FORM_ID',
    'RESOURCE_ORDER_FORM_ID',
    'BEEKEEPER_FORM_ID',
    'BEEKEEPER_RECEIPT_LINK',
    'BEEKEEPER_REGISTRATION_FORM_ID',
    'VEHICLE_VESSEL_VARIATION_FORM_ID',
    'LGA_RESOURCE_ORDER_FORM_ID',
  ],
}

function assertExists<T>(
  val: T,
  propName: string,
): asserts val is NonNullable<T> {
  if (val === undefined || val === null || Number.isNaN(val)) {
    const msg = `Config variable ${propName} has no value`
    throw new Error(msg)
  }
}

function isObject(val: unknown): val is Record<PropertyKey, unknown> {
  return isPlainObject(val)
}

function validateConfigValue<T>(
  value: T | undefined | null,
  propName: string,
): NonNullable<T> {
  const msg = `Config variable ${propName} has no value`
  assertExists(value, propName)

  if (isObject(value)) {
    return validateConfigObject(value)
  }

  if (
    (typeof value === 'string' && value === '') ||
    (typeof value === 'number' && value === -1)
  ) {
    throw new Error(msg)
  }

  return value
}

function validateConfigObject<T extends Record<PropertyKey, unknown>>(
  config: T,
): T {
  let hasErrors = false
  Object.entries(config).forEach(([key, value]) => {
    try {
      validateConfigValue(value, key)
    } catch (e) {
      const departmentWhitelist =
        whitelist[import.meta.env.REACT_APP_DEPARTMENT || '']
      if (departmentWhitelist && !departmentWhitelist.includes(key)) {
        console.error(e)
        hasErrors = true
      }
    }
  })

  if (import.meta.env.CI === 'true' && hasErrors) {
    throw new Error('CI Vars are missing, see ci log for details')
  }

  return config
}

const config = validateConfigObject({
  FORMS_APP_ID: parseInt(import.meta.env.REACT_APP_FORMS_APP_ID || ''),
  CUSTOMER_NAME: import.meta.env.REACT_APP_CUSTOMER || '',
  HOSTED_API_URL: import.meta.env.REACT_APP_OB_HOSTED_API_URL || '',
  GOOGLE_MAP_API_KEY: import.meta.env.REACT_APP_GOOGLE_MAP_API_KEY || '',
  ABN_AUTH_GUID: import.meta.env.REACT_APP_ABN_AUTH_GUID || '',
  FORMS: {
    BEEKEEPER_FORM_ID: parseInt(
      import.meta.env.REACT_APP_BEEKEEPER_FORM_ID || '',
    ),
    BEEKEEPER_REGISTRATION_FORM_ID: parseInt(
      import.meta.env.REACT_APP_BEEKEEPER_REGISTRATION_FORM_ID || '',
    ),
    FOOD_LICENCE_APPLICATION_FORM_ID: parseInt(
      import.meta.env.REACT_APP_FOOD_LICENCE_APPLICATION_FORM_ID || '',
    ),
    AI_FOOD_LICENCE_APPLICATION_FORM_ID: parseInt(
      import.meta.env.REACT_APP_AI_FOOD_LICENCE_APPLICATION_FORM_ID || '',
    ),
    FOOD_NOTIFICATION_FORM_ID: parseInt(
      import.meta.env.REACT_APP_FOOD_NOTIFICATION_FORM_ID || '',
    ),
    VEHICLE_VESSEL_VARIATION_FORM_ID: parseInt(
      import.meta.env.REACT_APP_VEHICLE_VESSEL_VARIATION_FORM_ID || '',
    ),
    COMPLAINTS_FORM_ID: parseInt(
      import.meta.env.REACT_APP_COMPLAINTS_FORM_ID || '',
    ),
    RESOURCE_ORDER_FORM_ID: parseInt(
      import.meta.env.REACT_APP_RESOURCE_ORDER_FORM_ID || '',
    ),
    SAVE_DRAFT_RECEIPT_FORM_ID: parseInt(
      import.meta.env.REACT_APP_SAVE_DRAFT_RECEIPT_FORM_ID || '',
    ),
    ACTIVITY_REPORT_FORM_ID: parseInt(
      import.meta.env.REACT_APP_ACTIVITY_REPORT_FORM_ID || '',
    ),
    LGA_RESOURCE_ORDER_FORM_ID: parseInt(
      import.meta.env.REACT_APP_LGA_RESOURCE_ORDER_FORM_ID || '',
    ),
  },
  BEEKEEPER_RECEIPT_LINK:
    import.meta.env.REACT_APP_BEEKEEPER_RECEIPT_LINK || '',
  DEPARTMENT: import.meta.env.REACT_APP_DEPARTMENT || '',
})

export default config
